import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { format } from "date-fns";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";

// Config
import { snackbarTypes } from "src/config/snackbar";
import { submissionStatus } from "src/config/values";
import { urls } from "src/config/routes";

// Actions
import { clearCaseDetail, getCaseDetail } from "src/state/caseDetail/actions";
import { toggleSnack } from "src/state/snackbar/actions";

// Hooks
import { useAppInsights } from "src/utils/telemetry/AppInsights";
import useTranslate from "src/hooks/useTranslate";

// Component
import LoadingSection from "src/components/LoadingSection";
import Text from "src/components/Text";
import ForceLogo from "./ForceLogo";
import sailogo from "../../../assets/logos/sai-logo.png";

const useStyles = makeStyles(theme => ({
  boxContainer: {
    padding: theme.spacing(3, 5),
    border: "1px solid #E0E0E0",
    backgroundColor: "#F7F7FA",
    borderRadius: theme.shape.borderRadius,
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2)
    }
  },
  boxRowTitle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start"
    }
  },
  boxForce: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  storeImg: {
    display: "block",
    height: "35px"
  },
  caseNumber: {
    fontSize: "30px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px"
    }
  },
  boxRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  title: {
    fontSize: "22px"
  },
  label: {
    fontSize: "14px",
    textTransform: "capitalize"
  },
  value: {
    fontSize: "14px",
    textAlign: "right"
  }
}));

const SummaryCard = ({ organizationId, requester, isSai }) => {
  const classes = useStyles();
  const { caseId } = useParams();
  const { trackException } = useAppInsights();
  const dispatch = useDispatch();
  const history = useHistory();
  const { data, isLoading, error } = useSelector(s => s.caseDetail);
  const getText = useTranslate();

  useEffect(() => {
    if (data && data.id !== caseId) {
      dispatch(clearCaseDetail);
    }

    if (error) {
      dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
      history.push(urls.cases);
    } else {
      if (!caseId || caseId === "undefined") {
        history.push(urls.cases);
        return;
      }

      const shouldGetData = !error && (!data || data.id !== caseId);
      const isReady = !isLoading;

      if (shouldGetData && isReady) {
        const onFail = callback => {
          trackException("Failed to get case details");
          if (callback) callback();
          history.push(urls.cases);
        };

        dispatch(getCaseDetail({ avoidError: true, caseId, onFail }));
      }
    }
  }, [caseId, data, dispatch, error, history, isLoading, trackException]);

  const submission = useMemo(
    () =>
      data?.submissions?.find(item => item.organization.id === organizationId),
    [organizationId, data]
  );

  if (isLoading || !data) {
    return (
      <Paper className={classes.boxContainer} elevation={0}>
        <LoadingSection normalHeight noBackground />
      </Paper>
    );
  }

  return (
    <Paper className={classes.boxContainer} elevation={0}>
      <Box className={classes.boxRowTitle}>
        <Box className={classes.boxForce}>
          {isSai && (
            <img className={classes.storeImg} src={sailogo} alt="SAI-logo" />
          )}
          {!isSai && (
            <>
              <ForceLogo
                src={submission?.organization.logoUri}
                alt={submission?.organization.name}
                color={submission?.organization.colorHex}
              />
              <Text type="heading2" className={classes.title}>
                {submission?.organization.name}
              </Text>
            </>
          )}
        </Box>
        <Text type="heading1" className={classes.caseNumber}>
          {data.number}
        </Text>
      </Box>
      {data.incident.location?.name && (
        <Box className={classes.boxRow}>
          <Text
            className={classes.label}
            text="common.valueLabels.incidentLocation"
          />
          <Text className={classes.value}>{data.incident.location.name}</Text>
        </Box>
      )}
      {data.incident.dateTime && (
        <Box className={classes.boxRow}>
          <Text
            className={classes.label}
            text="common.valueLabels.incidentDate"
          />
          <Text className={classes.value}>
            {format(
              new Date(data.incident.dateTime),
              getText("common.dateFormat.datetime")
            )}
          </Text>
        </Box>
      )}
      {requester?.fullName && (
        <Box className={classes.boxRow}>
          <Text
            className={classes.label}
            text="statementPage.summary.requestingOfficer"
          />
          <Text className={classes.value}>{requester.fullName}</Text>
        </Box>
      )}
      {submission && (
        <Box className={classes.boxRow}>
          <Text
            className={classes.label}
            text="statementPage.summary.forceRefNumber"
          />
          <Text
            className={classes.value}
            text={
              submission.status === submissionStatus.IN_PROGRESS
                ? "downloadCasePage.referencePendingReview"
                : null
            }
          >
            {submission.referenceNumber}
          </Text>
        </Box>
      )}
    </Paper>
  );
};

SummaryCard.propTypes = {
  organizationId: PropTypes.string.isRequired,
  requester: PropTypes.shape({
    fullName: PropTypes.string
  }).isRequired,
  isSai: PropTypes.bool.isRequired
};

export default SummaryCard;
